import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-brand-blue text-white hover:bg-blue-hover border-brand-blue',
  secondary: 'bg-bg-tertiary text-text-primary hover:bg-neutral-light dark:hover:bg-navy-semi-dark border-border-default',
  ghost: 'bg-transparent text-text-secondary hover:text-text-primary hover:bg-bg-tertiary border-transparent',
  destructive: 'bg-red-600 text-white hover:bg-red-700 border-red-600',
  danger: 'bg-red-600 text-white hover:bg-red-700 border-red-600',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-2.5 py-1 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-2.5 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', disabled, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-lg border font-medium',
          'transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-navy/50 dark:focus:ring-brand-lime/50',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'min-h-11 md:min-h-0',
          variantStyles[variant],
          sizeStyles[size],
          className
        )}
        disabled={disabled}
        {...props}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
